import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FIHRIST, KONULAR, basHarf, fihristBul, konuAdi, trDuz, type FihristKonu, type FihristMadde } from '../kilavuz/fihrist';
import { BOLUMLER } from '../kilavuz/icerik';
import YardimSekmeleri from '../kilavuz/YardimSekmeleri';
import { formatGun } from '../lib/api';
import { useAuth } from '../lib/auth';

/**
 * FİHRİST — panelde görünen her rakamın ve kavramın açıklaması (/fihrist).
 *
 * Solda liste (arama · harf şeridi · konu), sağda seçilen maddenin ayrıntısı:
 * nedir, nasıl hesaplanır + örnek, veri nereden gelir, hangi ekranda, ilgili
 * maddeler, son güncelleme. `/fihrist#ciro` doğrudan o maddeyi açar — «?»
 * balonundaki "Fihristte aç" ve sağ tuş menüsü buraya gelir.
 *
 * İçerik `kilavuz/fihrist.ts`tedir; bu sayfa yalnız gösterir. Maddeler role
 * göre süzülür (kılavuzla aynı kural: ekranı olmayan şey anlatılmaz).
 */
const ALFABE = 'ABCÇDEFGĞHIİJKLMNOÖPRSŞTUÜVYZ'.split('');
const sirala = (a: FihristMadde, b: FihristMadde) => a.baslik.localeCompare(b.baslik, 'tr');

/** Arama puanı: başlık > etiket > kısa açıklama > gövde. Eşleşmezse 0. */
function puan(m: FihristMadde, kelimeler: string[]): number {
  const baslik = trDuz(m.baslik); const etiket = trDuz(m.etiketler.join(' ')); const kisa = trDuz(m.kisa);
  const govde = trDuz([m.nedir, m.nasilHesaplanir, m.ornek, m.kaynak, ...(m.adimlar ?? [])].filter(Boolean).join(' '));
  let toplam = 0;
  for (const k of kelimeler) {
    const p = baslik.startsWith(k) ? 12 : baslik.includes(k) ? 8 : etiket.includes(k) ? 6 : kisa.includes(k) ? 3 : govde.includes(k) ? 1 : 0;
    if (p === 0) return 0;
    toplam += p;
  }
  return toplam;
}

export default function Fihrist() {
  const { can } = useAuth();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const [ara, setAra] = useState('');
  const [harf, setHarf] = useState<string | null>(null);
  const [konu, setKonu] = useState<FihristKonu | null>(null);
  const ayrintiRef = useRef<HTMLElement>(null);

  const gorunen = useMemo(() => FIHRIST.filter((m) => !m.cap || can(m.cap)), [can]);
  const goruluyor = (id: string) => gorunen.some((m) => m.id === id);
  const seciliId = decodeURIComponent(hash.replace('#', ''));
  const secili = goruluyor(seciliId) ? fihristBul(seciliId) : undefined;
  const sec = (id: string | null) => navigate(id ? `/fihrist#${id}` : '/fihrist', { replace: false });

  // Madde açılınca ayrıntı başa gelsin (dar ekranda liste yerine ayrıntı görünür).
  useEffect(() => {
    if (!secili) return;
    ayrintiRef.current?.scrollTo?.({ top: 0 });
    if (window.matchMedia('(max-width: 860px)').matches) ayrintiRef.current?.scrollIntoView({ block: 'start' });
  }, [secili?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const kelimeler = trDuz(ara).split(' ').filter(Boolean);
  const suzulmus = useMemo(() => {
    let xs = gorunen;
    if (konu) xs = xs.filter((m) => m.konu === konu);
    if (harf) xs = xs.filter((m) => basHarf(m.baslik) === harf);
    if (kelimeler.length === 0) return [...xs].sort(sirala);
    return xs.map((m) => ({ m, p: puan(m, kelimeler) })).filter((x) => x.p > 0).sort((a, b) => b.p - a.p || sirala(a.m, b.m)).map((x) => x.m);
  }, [gorunen, konu, harf, ara]); // eslint-disable-line react-hooks/exhaustive-deps

  const harfler = useMemo(() => new Set(gorunen.map((m) => basHarf(m.baslik))), [gorunen]);
  const sonGuncellenen = useMemo(
    () => [...gorunen].sort((a, b) => b.guncelleme.localeCompare(a.guncelleme) || sirala(a, b)).slice(0, 6),
    [gorunen],
  );
  const enYeni = sonGuncellenen[0]?.guncelleme;
  const suzgecYok = !konu && !harf && kelimeler.length === 0;
  // Süzgeç yokken konu başlıkları altında; arama ya da harf varken düz liste.
  const gruplar: { baslik: string | null; maddeler: FihristMadde[] }[] = suzgecYok
    ? KONULAR.map((k) => ({ baslik: k.ad, maddeler: suzulmus.filter((m) => m.konu === k.id) })).filter((g) => g.maddeler.length > 0)
    : [{ baslik: null, maddeler: suzulmus }];

  const kilavuzBolumu = secili?.kilavuz ? BOLUMLER.find((b) => b.id === secili.kilavuz && (!b.cap || can(b.cap))) : undefined;

  return (
    <>
      <YardimSekmeleri aktif="fihrist" />
      <div className="ccd-rapor-baslik">
        <div style={{ minWidth: 0 }}>
          <div className="ccd-eyebrow">Yardım · {gorunen.length} madde{enYeni ? ` · son güncelleme ${formatGun(enYeni)}` : ''}</div>
          <h1 className="ccd-title">Fihrist</h1>
          <div className="ccd-meta ccd-rapor-aciklama">
            Panelde gördüğünüz her rakam ve kavram: ne olduğu, nasıl hesaplandığı, verinin nereden geldiği.
            Ekranlardaki «?» işaretleri ve sağ tuş “Bu nedir?” buradaki maddeleri açar.
          </div>
        </div>
      </div>

      <div className={`ccd-fihrist${secili ? ' ccd-fihrist-acik' : ''}`}>
        {/* ── SOL: arama ve liste ─────────────────────────────────────────── */}
        <div className="ccd-fihrist-sol">
          <div className="ccd-fihrist-ara">
            <input className="ccd-input" type="search" value={ara} onChange={(e) => setAra(e.target.value)} autoFocus={!secili}
                   placeholder="Ara: ciro, vade, eşik, marj…" aria-label="Fihristte ara" />
          </div>

          <div className="ccd-fihrist-harf" role="group" aria-label="Baş harfe göre">
            <button type="button" className={harf == null ? 'ccd-is-active' : ''} onClick={() => setHarf(null)}>Tümü</button>
            {ALFABE.filter((h) => harfler.has(h)).map((h) => (
              <button key={h} type="button" className={harf === h ? 'ccd-is-active' : ''} aria-pressed={harf === h}
                      onClick={() => setHarf(harf === h ? null : h)}>{h}</button>
            ))}
          </div>

          <div className="ccd-fihrist-konu" role="group" aria-label="Konuya göre">
            {KONULAR.filter((k) => gorunen.some((m) => m.konu === k.id)).map((k) => (
              <button key={k.id} type="button" className={`ccd-fihrist-cip${konu === k.id ? ' ccd-is-active' : ''}`} aria-pressed={konu === k.id}
                      onClick={() => setKonu(konu === k.id ? null : k.id)}>{k.ad}</button>
            ))}
          </div>

          <nav className="ccd-fihrist-liste" aria-label="Fihrist maddeleri">
            {suzulmus.length === 0 && (
              <div className="ccd-empty">
                Eşleşen madde yok. Başka bir kelime deneyin ya da süzgeci kaldırın.
                <div style={{ marginTop: 8 }}>
                  <button type="button" className="ccd-btn ccd-btn-sm" onClick={() => { setAra(''); setHarf(null); setKonu(null); }}>Süzgeci temizle</button>
                </div>
              </div>
            )}
            {gruplar.map((g) => (
              <div key={g.baslik ?? 'duz'}>
                {g.baslik && <div className="ccd-fihrist-grup">{g.baslik}</div>}
                {g.maddeler.map((m) => (
                  <a key={m.id} href={`#${m.id}`} className={`ccd-fihrist-oge${m.id === secili?.id ? ' ccd-is-active' : ''}`}
                     aria-current={m.id === secili?.id ? 'true' : undefined}
                     onClick={(e) => { e.preventDefault(); sec(m.id); }}>
                    <span className="ccd-fihrist-oge-ad">{m.baslik}</span>
                    <span className="ccd-fihrist-oge-kisa">{m.kisa}</span>
                  </a>
                ))}
              </div>
            ))}
          </nav>
        </div>

        {/* ── SAĞ: madde ayrıntısı ───────────────────────────────────────── */}
        <article className="ccd-fihrist-ayrinti" ref={ayrintiRef} aria-live="polite">
          {!secili ? (
            <div className="ccd-fihrist-bos">
              <h2 className="ccd-heading">Bir madde seçin</h2>
              <p className="ccd-meta">
                Soldaki listeden seçin ya da arayın. Ekranlarda bir rakamın yanındaki «?» işaretine gelerek ya da rakama
                sağ tıklayıp (telefonda basılı tutup) “Bu nedir?” diyerek de buraya ulaşırsınız.
              </p>
              <div className="ccd-fihrist-etiket">Son güncellenenler</div>
              <div className="ccd-fihrist-son">
                {sonGuncellenen.map((m) => (
                  <a key={m.id} href={`#${m.id}`} className="ccd-fihrist-oge" onClick={(e) => { e.preventDefault(); sec(m.id); }}>
                    <span className="ccd-fihrist-oge-ad">{m.baslik}</span>
                    <span className="ccd-fihrist-oge-kisa">{formatGun(m.guncelleme)} · {konuAdi(m.konu)}</span>
                  </a>
                ))}
              </div>
              <div className="ccd-fihrist-etiket">Buradan başlayın</div>
              <div className="ccd-fihrist-ilgili">
                {['nasil-fihrist', 'ciro', 'tahsil-edilmemis', 'vadesi-gecen', 'birim-maliyet', 'kar-marj', 'esik', 'tarihsel-fatura'].filter(goruluyor).map((id) => (
                  <a key={id} href={`#${id}`} className="ccd-fihrist-cip" onClick={(e) => { e.preventDefault(); sec(id); }}>{fihristBul(id)!.baslik}</a>
                ))}
              </div>
            </div>
          ) : (
            <div key={secili.id}>
              <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm ccd-fihrist-geri" onClick={() => sec(null)}>← Listeye dön</button>
              <div className="ccd-eyebrow">{konuAdi(secili.konu)}</div>
              <h2 className="ccd-title ccd-fihrist-baslik">{secili.baslik}</h2>
              <p className="ccd-fihrist-kisa">{secili.kisa}</p>

              <div className="ccd-fihrist-etiket">Nedir</div>
              <p>{secili.nedir}</p>

              {secili.nasilHesaplanir && (
                <>
                  <div className="ccd-fihrist-etiket">Nasıl hesaplanır</div>
                  <p>{secili.nasilHesaplanir}</p>
                </>
              )}
              {secili.ornek && (
                <div className="ccd-fihrist-ornek">
                  <b>Örnek</b>
                  <span>{secili.ornek}</span>
                </div>
              )}

              {secili.adimlar && secili.adimlar.length > 0 && (
                <>
                  <div className="ccd-fihrist-etiket">Nasıl yapılır</div>
                  <ol className="ccd-fihrist-adim">
                    {secili.adimlar.map((a, i) => <li key={i}>{a}</li>)}
                  </ol>
                </>
              )}

              <div className="ccd-fihrist-etiket">Veri nereden gelir</div>
              <p>{secili.kaynak}</p>

              {secili.nerede.length > 0 && (
                <>
                  <div className="ccd-fihrist-etiket">Hangi ekranda</div>
                  <div className="ccd-fihrist-ilgili">
                    {secili.nerede.map((n) => <Link key={n.yol + n.ad} to={n.yol} className="ccd-fihrist-cip ccd-fihrist-cip-ekran">{n.ad} →</Link>)}
                    {kilavuzBolumu && (
                      <Link to={`/kilavuz#${kilavuzBolumu.id}`} className="ccd-fihrist-cip" title="Kullanım kılavuzunda adım adım anlatım">
                        Kılavuz › {kilavuzBolumu.baslik}
                      </Link>
                    )}
                  </div>
                </>
              )}

              {secili.ilgili.filter(goruluyor).length > 0 && (
                <>
                  <div className="ccd-fihrist-etiket">İlgili maddeler</div>
                  <div className="ccd-fihrist-ilgili">
                    {secili.ilgili.filter(goruluyor).map((id) => (
                      <a key={id} href={`#${id}`} className="ccd-fihrist-cip" title={fihristBul(id)!.kisa}
                         onClick={(e) => { e.preventDefault(); sec(id); }}>{fihristBul(id)!.baslik}</a>
                    ))}
                  </div>
                </>
              )}

              <p className="ccd-meta ccd-fihrist-dip">
                Son güncelleme: {formatGun(secili.guncelleme)} · Rakam beklediğinizden farklıysa sağ alttaki “Sorun Bildir” ile yazın.
              </p>
            </div>
          )}
        </article>
      </div>
    </>
  );
}
